// יצירות (09 §2).
import { z } from 'zod';
import { Id, Json, Key, Role, Ts, Usage, rowBase } from './common.ts';
import { InterfaceId } from './gate.ts';
import { Screen } from './block.ts';

/** 22 §1.1: סוג היצירה — report (חיבור בלוקים, 09), chat (שאלה–תשובה מעל פריסה), app (פרויקט מלא). sender/flow הוסרו (מיגרציה 0024). */
export const ArtifactKind = z.enum(['report', 'chat', 'app']);
export type ArtifactKind = z.infer<typeof ArtifactKind>;
/** תג הסוג במסך ובסיכומים (11). */
export const ARTIFACT_KIND_HE: Record<ArtifactKind, string> = { report: 'דוח', chat: 'שיחה', app: 'אפליקציה' };
/** 22 §1.6: שני צירי הגרסה בסיכום — "גרסה 4 (אפליקציה V2)"; בלי סוג/גרסת סוג — "גרסה 4". */
export const versionHe = (version: number, kind?: ArtifactKind | null, kindVersion?: number | null) =>
  kind && kindVersion ? `גרסה ${version} (${ARTIFACT_KIND_HE[kind]} V${kindVersion})` : `גרסה ${version}`;
/** 22 §1.5: תוצאת בנייה של גרסת app (קונטיינר build) — null ל-report/chat. */
export const ArtifactBuild = z.object({
  ok: z.boolean(),
  steps: z.array(z.object({ name: z.string(), ok: z.boolean(), ms: z.number().int().nonnegative(), tail: z.string().optional() })),
  builtAt: z.string().optional(),
});
export type ArtifactBuild = z.infer<typeof ArtifactBuild>;

export const ArtifactStatus = z.enum(['draft', 'published', 'archived']);
export type ArtifactStatus = z.infer<typeof ArtifactStatus>;

export const RunAs = z.enum(['runner', 'creator']);
export type RunAs = z.infer<typeof RunAs>;

/** sys.artifact */
export const Artifact = z.object({
  ...rowBase,
  slug: Key,
  nameHe: z.string(),
  /** 22: הסוג של הגרסה החיה (denormalized לרשימות). */
  kind: ArtifactKind,
  descriptionHe: z.string(),
  creatorUserId: Id,
  runAs: RunAs,
  minRole: Role,
  currentVersion: z.number().int().positive().nullable(),
  status: ArtifactStatus,
  /** דריסת גבולות ריצה (רק admin). */
  limits: z.record(z.string(), z.unknown()).nullable(),
});
export type Artifact = z.infer<typeof Artifact>;

export const ArtifactUses = z.object({
  spreads: z.array(z.string()),
  artifacts: z.array(z.string()),
  interfaces: z.array(InterfaceId),
  /** 14: hosts שהקוד מצהיר (`export const web = ['api.x.il']`) — ההיקף המבוקש לממשק web. */
  hosts: z.array(z.string()).optional(),
  /** 20: נמענים שהקוד מצהיר (`export const mail = { to: ['dan@x.co.il', '*@x.co.il'] }`) — ההיקף המבוקש ל-mail.send. */
  recipients: z.array(z.string()).optional(),
  /** 17: מפתחות מסדי היצירה (`ctx.db('<ליטרל>')` / `export const dbs = [...]`) — נבדקים מול grants בתחילת ריצה. */
  dbs: z.array(z.string().regex(/^[a-z][a-z0-9_]{1,30}$/)).optional(),
});
export type ArtifactUses = z.infer<typeof ArtifactUses>;

/** 17 §1.2: מפתח מסד ליצירה — ליטרל אנגלי כמו slug. */
export const ArtifactDbKey = z.string().regex(/^[a-z][a-z0-9_]{1,30}$/);
/** sys.artifact_db */
export const ArtifactDb = z.object({
  ...rowBase,
  key: ArtifactDbKey,
  nameHe: z.string(),
  noteHe: z.string().nullable(),
  dbName: z.string(),
  ownerRole: z.string(),
  createdBy: Id.nullable(),
  limits: z.object({ softMb: z.number().positive(), hardMb: z.number().positive().optional() }),
  bytes: z.number().nullable(),
  bytesAt: z.date().nullable(),
  status: z.enum(['active', 'over_limit', 'dropped']),
  droppedAt: z.date().nullable(),
});
export type ArtifactDb = z.infer<typeof ArtifactDb>;
/** sys.artifact_db_grant (PK מורכב, בלי id). */
export const ArtifactDbGrant = z.object({
  instanceId: Id,
  artifactId: Id,
  dbId: Id,
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
  grantedBy: Id.nullable(),
  grantedAt: z.date(),
  revokedAt: z.date().nullable(),
  revokedBy: Id.nullable(),
});
export type ArtifactDbGrant = z.infer<typeof ArtifactDbGrant>;
/** 17 §1.4: מה שקונטיינר הריצה מקבל ב-creds/<c>/db.json — `{ dbs: { <key>: DbCred } }`. לעולם לא נשמר בשורה. */
export const DbCred = z.object({ host: z.string(), port: z.number().int(), database: z.string(), user: z.string(), password: z.string(), validUntil: z.string() });
export type DbCred = z.infer<typeof DbCred>;

/** sys.artifact_version */
/** בדיקת מבנה של גרסה (22 §1.8, artifacts-22-migrate-existing): ok / upgraded (→ to) / blocked (reasons) / pending (שירות הבנייה לא ענה). */
export const ArtifactStructureStatus = z.enum(['ok', 'upgraded', 'blocked', 'pending']);
export const ArtifactStructureReason = z.object({ code: z.string(), textHe: z.string(), file: z.string().optional(), line: z.number().int().optional() });
export type ArtifactStructureReason = z.infer<typeof ArtifactStructureReason>;
export const ArtifactStructure = z.object({
  rev: z.number().int().positive(),
  status: ArtifactStructureStatus,
  at: z.string(),
  reasons: z.array(ArtifactStructureReason).optional(),
  /** upgraded: הגרסה החדשה; ok בגרסה שנוצרה בשדרוג: from = הראש הקודם. */
  to: z.number().int().positive().optional(),
  from: z.number().int().positive().optional(),
});
export type ArtifactStructure = z.infer<typeof ArtifactStructure>;

export const ArtifactVersion = z.object({
  ...rowBase,
  artifactId: Id,
  version: z.number().int().positive(),
  code: z.string(),
  paramsSchema: Json,
  uses: ArtifactUses,
  createdBy: Id,
  noteHe: z.string().nullable(),
  /** 16: {action: JSON Schema של params} מ-`export const api`. */
  apiSchema: z.record(z.string(), Json),
  /** 22 §1.6: הסוג וגרסת הסוג — נעוצים לגרסת היצירה (rollback מחזיר גם אותם). */
  kind: ArtifactKind,
  kindVersion: z.number().int().positive(),
  /** 22 §1.5: תוצאת הבנייה (app בלבד). */
  build: ArtifactBuild.nullable(),
  /** 22 §2: תיקיית הגרסה יחסית ל-AIBIFY_DATA_ROOT (`artifacts/<id>/v<n>`); null = עוד לא נכתבה לדיסק. */
  filesRef: z.string().nullable(),
  /** 25 §2: כישורי הליבה שהועתקו לגרסה — העתק של הרישום שבקבצים; rollback מחזיר גם אותו. */
  core: z.array(z.object({ key: z.string(), version: z.number().int().positive() })),
  /** OpenSpec artifacts-22-migrate-existing: תוצאת בדיקת המבנה של גרסת הראש (null = לא נבדקה). */
  structure: ArtifactStructure.nullable(),
});
export type ArtifactVersion = z.infer<typeof ArtifactVersion>;

/** 16: שם פעולה ב-`export const api` (ליטרל). */
export const ApiActionName = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/);
export const ArtifactCallStatus = z.enum(['running', 'done', 'failed', 'timeout', 'denied', 'consent_pending', 'canceled']);
export type ArtifactCallStatus = z.infer<typeof ArtifactCallStatus>;
/** קודי שגיאה של ab.call (16 §1.2) — מה שהדפדפן מקבל. */
export const AbCallErrorCode = z.enum(['invalid_args', 'denied', 'not_found', 'consent_pending', 'rate_limited', 'timeout', 'unavailable', 'failed', 'store_full']);
export type AbCallErrorCode = z.infer<typeof AbCallErrorCode>;
/** sys.artifact_call */
export const ArtifactCall = z.object({
  ...rowBase,
  /** artifacts-report-lookup: null = lookup בלי ריצה. */
  runId: Id.nullable(),
  artifactId: Id,
  version: z.number().int().positive(),
  action: ApiActionName,
  userId: Id,
  runAsUserId: Id,
  status: ArtifactCallStatus,
  ms: z.number().int().nullable(),
  argsBytes: z.number().int(),
  resultBytes: z.number().int().nullable(),
  dbs: z.array(z.string()),
  toolCalls: z.number().int(),
  errorHe: z.string().nullable(),
  consentId: Id.nullable(),
  finishedAt: Ts.nullable(),
  /** app@2 (apps-app2-cached-calls): מטמון של פעולת קריאה עם cache. */
  argsHash: z.string().nullable(),
  result: Json.nullable(),
  asOf: Ts.nullable(),
  /** artifacts-report-lookup: 'call' (ab.call) / 'lookup' (חיפוש בטופס). */
  origin: z.enum(['call', 'lookup']),
});
export type ArtifactCall = z.infer<typeof ArtifactCall>;

export const ArtifactRunStatus = z.enum(['queued', 'running', 'waiting_consent', 'done', 'failed', 'timeout', 'canceled']);
export type ArtifactRunStatus = z.infer<typeof ArtifactRunStatus>;

/** sys.artifact_run */
export const ArtifactRun = z.object({
  ...rowBase,
  artifactId: Id,
  version: z.number().int().positive(),
  userId: Id,
  runAsUserId: Id,
  parentRunId: Id.nullable(),
  conversationId: Id.nullable(),
  params: Json,
  status: ArtifactRunStatus,
  startedAt: Ts.nullable(),
  finishedAt: Ts.nullable(),
  output: Screen.nullable(),
  log: z.string().nullable(),
  consentIds: z.array(Id),
  asOf: Ts.nullable(),
  usage: Usage.nullable(),
  errorHe: z.string().nullable(),
  containerId: z.string().nullable(),
  /** 17: מפתחות המסדים שהורכבו לריצה. */
  dbs: z.array(z.string()),
  /** 19: הריצה הופעלה מטריגר (sys.trigger_run) — השער לא פותח לה קונסנט. */
  triggerRunId: Id.nullable(),
  /** artifacts-report-lookup: {param: labelHe} של שדות בחירה עם חיפוש; null = אין. */
  paramLabels: z.record(z.string(), z.string()).nullable(),
  /** artifacts-empty-screen (0051): run() החזיר אפס בלוקים; הבעיות מ-normalizeScreen; שליחות שדולגו (אין תוכן). */
  emptyScreen: z.boolean(),
  problems: z.array(z.string()).nullable(),
  skippedHe: z.array(z.string()).nullable(),
});
export type ArtifactRun = z.infer<typeof ArtifactRun>;

export const InterfaceMode = z.enum(['auto', 'consent']);
export type InterfaceMode = z.infer<typeof InterfaceMode>;

/** sys.artifact_interface */
export const ArtifactInterface = z.object({
  ...rowBase,
  artifactId: Id,
  interfaceId: InterfaceId,
  mode: InterfaceMode,
  setBy: Id,
});
export type ArtifactInterface = z.infer<typeof ArtifactInterface>;

/** sys.artifact_share — יצירה פרטית ליוצר; שורה = צפייה + הרצה (לעולם לא עריכה): משתמש מסוים, או everyone (מסונן לפי min_role). */
export const ArtifactShare = z.object({
  ...rowBase,
  artifactId: Id,
  userId: Id.nullable(),
  everyone: z.boolean(),
  sharedBy: Id,
});
export type ArtifactShare = z.infer<typeof ArtifactShare>;

/** sys.file */
export const File = z.object({
  ...rowBase,
  runId: Id,
  name: z.string(),
  mime: z.string(),
  bytes: z.number().int().nonnegative(),
  storageRef: z.string(),
  expiresAt: Ts,
});
export type File = z.infer<typeof File>;
